import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import UserService from "services/user.service";
import RoleService from "services/role.service";
import { toast } from "react-toastify";

export default function UserRegister() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);

  // État pour gérer les erreurs de validation
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const [user, setUser] = useState({
    id: null,
    login: "",
    firstName: "",
    lastName: "",
    email: "",
    activated: true,
    langKey: "fr",
    authorities: ["ROLE_USER"],
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const rolesRes = await RoleService.getAll();
      setAvailableRoles(rolesRes.data);

      if (isEditMode) {
        const userRes = await UserService.getById(Number(id));
        setUser(userRes.data);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement de l'utilisateur");
    } finally {
      setLoading(false);
    }
  };

  // Validation des champs
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!user.firstName) newErrors.firstName = "Le prénom est requis";
    if (!user.lastName) newErrors.lastName = "Le nom est requis";
    if (!user.email) {
      newErrors.email = "L'email est requis";
    } else if (!/\S+@\S+\.\S+/.test(user.email)) {
      newErrors.email = "Format d'email invalide";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const toggleRole = (roleName) => {
    setUser((prev) => ({
      ...prev,
      authorities: prev.authorities.includes(roleName)
        ? prev.authorities.filter((r) => r !== roleName)
        : [...prev.authorities, roleName],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Veuillez remplir les champs obligatoires");
      return;
    }

    try {
      setLoading(true);
      if (isEditMode) {
        await UserService.update(user);
        toast.success("Utilisateur mis à jour");
        window.dispatchEvent(new CustomEvent("userProfileUpdated"));
      } else {
        await UserService.create(user);
        toast.success("Utilisateur créé");
      }
      history.push("/admin/users");
    } catch (error) {
      toast.error("Échec de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  // Fonction utilitaire pour mettre à jour le state et effacer l'erreur
  const handleInputChange = (field: string, value: string) => {
    setUser({ ...user, [field]: value });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <div className="flex flex-col items-center justify-center p-4 w-full min-h-screen">
      <div className="w-full max-w-4xl">
        <div className="relative flex flex-col bg-[#0B1222] border border-white/10 rounded-[1.5rem] shadow-2xl overflow-hidden">
          <div className="bg-[#0F172A]/60 px-8 py-5 border-b border-white/5 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center border border-[#D4AF37]/20">
                <i
                  className={`fas ${isEditMode ? "fa-edit" : "fa-user-plus"} text-[#D4AF37] text-xs`}
                ></i>
              </div>
              <h2 className="text-white text-sm font-black uppercase tracking-[0.15em]">
                {isEditMode ? `Modifier : ${user.login}` : "Nouvel Utilisateur"}
              </h2>
            </div>
            <button
              onClick={() => history.goBack()}
              className="text-slate-500 hover:text-white transition-colors"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          <form className="p-8 lg:p-10" onSubmit={handleSubmit} noValidate>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
              {/* PRENOM */}
              <div className="space-y-1.5">
                <label className="text-[9px] text-[#D4AF37] uppercase font-black tracking-widest ml-1">
                  Prénom
                </label>
                <input
                  type="text"
                  value={user.firstName}
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                  className={`w-full bg-[#161F32]/50 border ${errors.firstName ? "border-red-500" : "border-white/5"} rounded-xl px-4 py-2.5 text-sm text-white focus:border-[#D4AF37] outline-none transition-all`}
                  placeholder="Jean"
                />
                {errors.firstName && (
                  <span className="text-red-500 text-[8px] font-bold uppercase ml-1 block">
                    {errors.firstName}
                  </span>
                )}
              </div>

              {/* NOM */}
              <div className="space-y-1.5">
                <label className="text-[9px] text-[#D4AF37] uppercase font-black tracking-widest ml-1">
                  Nom
                </label>
                <input
                  type="text"
                  value={user.lastName}
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                  className={`w-full bg-[#161F32]/50 border ${errors.lastName ? "border-red-500" : "border-white/5"} rounded-xl px-4 py-2.5 text-sm text-white focus:border-[#D4AF37] outline-none transition-all`}
                  placeholder="Dupont"
                />
                {errors.lastName && (
                  <span className="text-red-500 text-[8px] font-bold uppercase ml-1 block">
                    {errors.lastName}
                  </span>
                )}
              </div>

              {/* EMAIL */}
              <div className="md:col-span-2 space-y-1.5">
                <label className="text-[9px] text-[#D4AF37] uppercase font-black tracking-widest ml-1">
                  Adresse E-mail
                </label>
                <input
                  type="email"
                  value={user.email}
                  disabled={isEditMode}
                  onChange={(e) => {
                    const val = e.target.value;
                    setUser({ ...user, email: val, login: val });
                    if (errors.email)
                      setErrors((prev) => ({ ...prev, email: "" }));
                  }}
                  className={`w-full bg-[#161F32]/50 border ${errors.email ? "border-red-500" : "border-white/5"} rounded-xl px-4 py-2.5 text-sm text-white focus:border-[#D4AF37] outline-none transition-all disabled:opacity-50`}
                  placeholder="contact@eglise.com"
                />
                {errors.email && (
                  <span className="text-red-500 text-[8px] font-bold uppercase ml-1 block">
                    {errors.email}
                  </span>
                )}
              </div>

              {/* RÔLES DYNAMIQUES */}
              <div className="md:col-span-2 mt-2">
                <label className="text-[9px] text-[#D4AF37] uppercase font-black tracking-[0.2em] mb-3 block">
                  Privilèges Accès
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {availableRoles.map((role) => {
                    const roleName =
                      typeof role === "string" ? role : role.name;
                    const active = user.authorities.includes(roleName);
                    return (
                      <div
                        key={roleName}
                        onClick={() => toggleRole(roleName)}
                        className={`cursor-pointer px-4 py-3 rounded-xl border transition-all flex items-center gap-3 ${
                          active
                            ? "bg-[#D4AF37] border-[#D4AF37] text-[#0B1222]"
                            : "bg-[#161F32]/30 border-white/5 text-slate-500 hover:border-white/20"
                        }`}
                      >
                        <i
                          className={`fas ${roleName === "ROLE_ADMIN" ? "fa-shield-alt" : "fa-user"} text-xs`}
                        ></i>
                        <span className="text-[10px] font-black uppercase tracking-tight">
                          {roleName.replace("ROLE_", "")}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ACTIONS */}
              <div className="md:col-span-2 flex items-center justify-between pt-6 mt-4 border-t border-white/5">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="active"
                    checked={user.activated}
                    onChange={(e) =>
                      setUser({ ...user, activated: e.target.checked })
                    }
                    className="w-4 h-4 rounded border-white/10 bg-slate-800 accent-[#D4AF37]"
                  />
                  <label
                    htmlFor="active"
                    className="text-[10px] text-slate-400 uppercase font-bold tracking-widest cursor-pointer"
                  >
                    Compte actif
                  </label>
                </div>

                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => history.goBack()}
                    className="px-6 py-2.5 text-[10px] font-black uppercase text-slate-500 hover:text-white transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-[#D4AF37] text-[#0B1222] px-8 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-[0.2em] shadow-lg shadow-[#D4AF37]/10 hover:scale-105 transition-all disabled:opacity-50"
                  >
                    {loading
                      ? "Chargement..."
                      : isEditMode
                        ? "Enregistrer"
                        : "Créer l'accès"}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
